import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Warehouse,
  Truck,
  Building2,
  Package,
  ShoppingCart,
  ArrowLeftRight,
  AlertTriangle,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  Send,
  Check,
  X,
  Eye,
  FileText,
  DollarSign,
  TrendingUp,
  Calendar,
  Star,
  CheckCircle,
  Clock,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =============== WAREHOUSES TAB ===============
const WarehousesTab = ({ t }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', code: '', warehouse_type: 'branch', address: '', city: '',
    phone: '', email: '', manager_name: '', has_cold_storage: false,
    has_controlled_substances: false, notes: ''
  });

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/warehouses`);
      setWarehouses(response.data || []);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchWarehouses(); }, [fetchWarehouses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await axios.put(`${API_URL}/api/warehouses/${editItem.id}`, formData);
        toast.success('Warehouse updated');
      } else {
        await axios.post(`${API_URL}/api/warehouses`, formData);
        toast.success('Warehouse created');
      }
      setShowDialog(false);
      setEditItem(null);
      resetForm();
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving warehouse');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', code: '', warehouse_type: 'branch', address: '', city: '',
      phone: '', email: '', manager_name: '', has_cold_storage: false,
      has_controlled_substances: false, notes: ''
    });
  };

  const openEdit = (wh) => {
    setEditItem(wh);
    setFormData({
      name: wh.name || '', code: wh.code || '', warehouse_type: wh.warehouse_type || 'branch',
      address: wh.address || '', city: wh.city || '', phone: wh.phone || '',
      email: wh.email || '', manager_name: wh.manager_name || '',
      has_cold_storage: wh.has_cold_storage || false,
      has_controlled_substances: wh.has_controlled_substances || false,
      notes: wh.notes || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this warehouse?')) return;
    try {
      await axios.delete(`${API_URL}/api/warehouses/${id}`);
      toast.success('Warehouse deleted');
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting warehouse');
    }
  };

  const getStatusBadge = (status) => {
    const colors = { active: 'bg-green-100 text-green-800', inactive: 'bg-gray-100 text-gray-800', maintenance: 'bg-yellow-100 text-yellow-800' };
    return <Badge className={colors[status] || 'bg-gray-100'}>{t(`enterprise.warehouses.statuses.${status}`)}</Badge>;
  };

  const getTypeBadge = (type) => {
    const colors = { main: 'bg-blue-100 text-blue-800', branch: 'bg-purple-100 text-purple-800', distribution: 'bg-indigo-100 text-indigo-800', cold_storage: 'bg-cyan-100 text-cyan-800', mobile: 'bg-orange-100 text-orange-800' };
    return <Badge className={colors[type] || 'bg-gray-100'}>{t(`enterprise.warehouses.types.${type}`)}</Badge>;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('enterprise.warehouses.title')}</h3>
          <p className="text-sm text-gray-500">{t('enterprise.warehouses.subtitle')}</p>
        </div>
        <Button onClick={() => { resetForm(); setEditItem(null); setShowDialog(true); }} className="bg-[#E63946]">
          <Plus className="h-4 w-4 mr-2" />{t('enterprise.warehouses.newWarehouse')}
        </Button>
      </div>

      {warehouses.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500"><Warehouse className="h-12 w-12 mx-auto mb-4 opacity-50" />{t('enterprise.warehouses.empty')}</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {warehouses.map(wh => (
            <Card key={wh.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Warehouse className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{wh.name}</h4>
                        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{wh.code}</code>
                        {getTypeBadge(wh.warehouse_type)}
                        {getStatusBadge(wh.status)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {wh.address && <span>{wh.address}</span>}
                        {wh.city && <span> • {wh.city}</span>}
                        {wh.manager_name && <span> • {t('enterprise.warehouses.manager')}: {wh.manager_name}</span>}
                      </div>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span><strong>{wh.total_items || 0}</strong> {t('enterprise.warehouses.items')}</span>
                        <span><strong>${(wh.total_value || 0).toLocaleString()}</strong> {t('enterprise.warehouses.value')}</span>
                        {wh.has_cold_storage && <Badge variant="outline" className="text-cyan-600"><Package className="h-3 w-3 mr-1" />{t('enterprise.warehouses.coldStorage')}</Badge>}
                        {wh.has_controlled_substances && <Badge variant="outline" className="text-red-600"><AlertTriangle className="h-3 w-3 mr-1" />{t('enterprise.warehouses.controlledSubstances')}</Badge>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(wh)}><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(wh.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Warehouse' : t('enterprise.warehouses.newWarehouse')}</DialogTitle>
            <DialogDescription>{t('enterprise.warehouses.form.title')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.warehouses.name')} *</Label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required /></div>
              <div><Label>{t('enterprise.warehouses.code')} *</Label><Input value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} required maxLength={10} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.warehouses.type')}</Label>
                <Select value={formData.warehouse_type} onValueChange={v => setFormData({...formData, warehouse_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['main', 'branch', 'distribution', 'cold_storage', 'mobile'].map(type => (
                      <SelectItem key={type} value={type}>{t(`enterprise.warehouses.types.${type}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t('enterprise.warehouses.city')}</Label><Input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} /></div>
            </div>
            <div><Label>{t('enterprise.warehouses.form.address')}</Label><Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.warehouses.form.phone')}</Label><Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} /></div>
              <div><Label>{t('enterprise.warehouses.form.email')}</Label><Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} /></div>
            </div>
            <div><Label>{t('enterprise.warehouses.manager')}</Label><Input value={formData.manager_name} onChange={e => setFormData({...formData, manager_name: e.target.value})} /></div>
            <div className="flex gap-6">
              <label className="flex items-center gap-2"><Checkbox checked={formData.has_cold_storage} onCheckedChange={c => setFormData({...formData, has_cold_storage: c})} />{t('enterprise.warehouses.coldStorage')}</label>
              <label className="flex items-center gap-2"><Checkbox checked={formData.has_controlled_substances} onCheckedChange={c => setFormData({...formData, has_controlled_substances: c})} />{t('enterprise.warehouses.controlledSubstances')}</label>
            </div>
            <div><Label>{t('enterprise.warehouses.form.notes')}</Label><Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E63946]">{editItem ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =============== SUPPLIERS TAB ===============
const SuppliersTab = ({ t }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '', code: '', legal_name: '', tax_id: '', contact_name: '',
    contact_email: '', contact_phone: '', address: '', city: '', country: 'Colombia',
    payment_terms: 'net_30', credit_limit: 0, lead_time_days: 7, minimum_order: 0, notes: ''
  });

  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/suppliers`);
      setSuppliers(response.data?.suppliers || []);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await axios.put(`${API_URL}/api/suppliers/${editItem.id}`, formData);
        toast.success('Supplier updated');
      } else {
        await axios.post(`${API_URL}/api/suppliers`, formData);
        toast.success('Supplier created');
      }
      setShowDialog(false);
      setEditItem(null);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving supplier');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', code: '', legal_name: '', tax_id: '', contact_name: '',
      contact_email: '', contact_phone: '', address: '', city: '', country: 'Colombia',
      payment_terms: 'net_30', credit_limit: 0, lead_time_days: 7, minimum_order: 0, notes: ''
    });
  };

  const openEdit = (supplier) => {
    setEditItem(supplier);
    setFormData({
      name: supplier.name || '', code: supplier.code || '', legal_name: supplier.legal_name || '',
      tax_id: supplier.tax_id || '', contact_name: supplier.contact_name || '',
      contact_email: supplier.contact_email || '', contact_phone: supplier.contact_phone || '',
      address: supplier.address || '', city: supplier.city || '', country: supplier.country || 'Colombia',
      payment_terms: supplier.payment_terms || 'net_30', credit_limit: supplier.credit_limit || 0,
      lead_time_days: supplier.lead_time_days || 7, minimum_order: supplier.minimum_order || 0,
      notes: supplier.notes || ''
    });
    setShowDialog(true);
  };

  const getStatusBadge = (status) => {
    const colors = { active: 'bg-green-100 text-green-800', inactive: 'bg-gray-100 text-gray-800', blocked: 'bg-red-100 text-red-800', pending_approval: 'bg-yellow-100 text-yellow-800' };
    return <Badge className={colors[status] || 'bg-gray-100'}>{t(`enterprise.suppliers.statuses.${status}`)}</Badge>;
  };

  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <Star key={i} className={`h-3 w-3 ${i < Math.round(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
    ));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('enterprise.suppliers.title')}</h3>
          <p className="text-sm text-gray-500">{t('enterprise.suppliers.subtitle')}</p>
        </div>
        <Button onClick={() => { resetForm(); setEditItem(null); setShowDialog(true); }} className="bg-[#E63946]">
          <Plus className="h-4 w-4 mr-2" />{t('enterprise.suppliers.newSupplier')}
        </Button>
      </div>

      {suppliers.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500"><Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />{t('enterprise.suppliers.empty')}</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {suppliers.map(supplier => (
            <Card key={supplier.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{supplier.name}</h4>
                        <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{supplier.code}</code>
                        {getStatusBadge(supplier.status)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {supplier.contact_name && <span>{supplier.contact_name}</span>}
                        {supplier.contact_email && <span> • {supplier.contact_email}</span>}
                        {supplier.contact_phone && <span> • {supplier.contact_phone}</span>}
                      </div>
                      <div className="flex gap-4 mt-2 text-sm items-center">
                        <Badge variant="outline">{t(`enterprise.suppliers.terms.${supplier.payment_terms}`)}</Badge>
                        <span>{supplier.lead_time_days} {t('enterprise.valuation.days')}</span>
                        <div className="flex items-center gap-1">{renderStars(supplier.performance?.overall_rating || 5)}</div>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(supplier)}><Edit className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editItem ? 'Edit Supplier' : t('enterprise.suppliers.newSupplier')}</DialogTitle>
            <DialogDescription>{t('enterprise.suppliers.form.title')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.suppliers.name')} *</Label><Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required /></div>
              <div><Label>{t('enterprise.suppliers.code')} *</Label><Input value={formData.code} onChange={e => setFormData({...formData, code: e.target.value.toUpperCase()})} required maxLength={10} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.suppliers.form.legalName')}</Label><Input value={formData.legal_name} onChange={e => setFormData({...formData, legal_name: e.target.value})} /></div>
              <div><Label>{t('enterprise.suppliers.form.taxId')}</Label><Input value={formData.tax_id} onChange={e => setFormData({...formData, tax_id: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.suppliers.contact')}</Label><Input value={formData.contact_name} onChange={e => setFormData({...formData, contact_name: e.target.value})} /></div>
              <div><Label>{t('enterprise.suppliers.phone')}</Label><Input value={formData.contact_phone} onChange={e => setFormData({...formData, contact_phone: e.target.value})} /></div>
            </div>
            <div><Label>{t('enterprise.suppliers.email')}</Label><Input type="email" value={formData.contact_email} onChange={e => setFormData({...formData, contact_email: e.target.value})} /></div>
            <div><Label>{t('enterprise.suppliers.form.address')}</Label><Input value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.suppliers.form.city')}</Label><Input value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} /></div>
              <div><Label>{t('enterprise.suppliers.form.country')}</Label><Input value={formData.country} onChange={e => setFormData({...formData, country: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.suppliers.paymentTerms')}</Label>
                <Select value={formData.payment_terms} onValueChange={v => setFormData({...formData, payment_terms: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['immediate', 'net_15', 'net_30', 'net_45', 'net_60', 'net_90'].map(term => (
                      <SelectItem key={term} value={term}>{t(`enterprise.suppliers.terms.${term}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t('enterprise.suppliers.form.leadTime')}</Label><Input type="number" value={formData.lead_time_days} onChange={e => setFormData({...formData, lead_time_days: parseInt(e.target.value) || 0})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.suppliers.form.creditLimit')}</Label><Input type="number" value={formData.credit_limit} onChange={e => setFormData({...formData, credit_limit: parseFloat(e.target.value) || 0})} /></div>
              <div><Label>{t('enterprise.suppliers.form.minOrder')}</Label><Input type="number" value={formData.minimum_order} onChange={e => setFormData({...formData, minimum_order: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div><Label>{t('enterprise.suppliers.form.notes')}</Label><Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E63946]">{editItem ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =============== PURCHASE ORDERS TAB ===============
const PurchaseOrdersTab = ({ t }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [medications, setMedications] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showReceiveSheet, setShowReceiveSheet] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [receivingStatus, setReceivingStatus] = useState(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [scanQuantity, setScanQuantity] = useState(1);
  const [batchNumber, setBatchNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [scanHistory, setScanHistory] = useState([]);
  const [formData, setFormData] = useState({
    supplier_id: '', supplier_name: '', warehouse_id: '', warehouse_name: '',
    expected_date: '', notes: '', items: []
  });
  const [newItem, setNewItem] = useState({ medication_id: '', medication_name: '', quantity: 1, unit_price: 0 });

  const fetchData = useCallback(async () => {
    try {
      const [poRes, whRes, supRes, medRes] = await Promise.all([
        axios.get(`${API_URL}/api/purchase-orders`),
        axios.get(`${API_URL}/api/warehouses`),
        axios.get(`${API_URL}/api/suppliers`),
        axios.get(`${API_URL}/api/medications/catalog`)
      ]);
      setOrders(poRes.data?.purchase_orders || []);
      setWarehouses(whRes.data || []);
      setSuppliers(supRes.data?.suppliers || []);
      setMedications(medRes.data || []);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/purchase-orders`, formData);
      toast.success('Purchase order created');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating PO');
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '', supplier_name: '', warehouse_id: '', warehouse_name: '',
      expected_date: '', notes: '', items: []
    });
    setNewItem({ medication_id: '', medication_name: '', quantity: 1, unit_price: 0 });
  };

  const addItem = () => {
    if (!newItem.medication_id || newItem.quantity <= 0) return;
    setFormData({
      ...formData,
      items: [...formData.items, { ...newItem, unit: 'unidad', discount_percent: 0, tax_percent: 19 }]
    });
    setNewItem({ medication_id: '', medication_name: '', quantity: 1, unit_price: 0 });
  };

  const removeItem = (idx) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== idx) });
  };

  const handleAction = async (poId, action) => {
    try {
      if (action === 'cancel') {
        const reason = prompt('Reason for cancellation:');
        if (!reason) return;
        await axios.put(`${API_URL}/api/purchase-orders/${poId}/cancel?reason=${encodeURIComponent(reason)}`);
      } else {
        await axios.put(`${API_URL}/api/purchase-orders/${poId}/${action}`);
      }
      toast.success('Action completed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  // Open receiving sheet for a PO
  const openReceiveSheet = async (po) => {
    setSelectedPO(po);
    setScanHistory([]);
    setBarcodeInput('');
    setScanQuantity(1);
    setBatchNumber('');
    setExpirationDate('');
    
    try {
      const response = await axios.get(`${API_URL}/api/purchase-orders/${po.id}/receiving-status`);
      setReceivingStatus(response.data);
      setShowReceiveSheet(true);
    } catch (error) {
      toast.error('Error loading receiving status');
    }
  };

  // Handle barcode scan
  const handleBarcodeScan = async (e) => {
    e.preventDefault();
    if (!barcodeInput.trim() || !selectedPO) return;
    
    try {
      const params = new URLSearchParams({
        barcode_value: barcodeInput.trim(),
        quantity: scanQuantity
      });
      if (batchNumber) params.append('batch_number', batchNumber);
      if (expirationDate) params.append('expiration_date', expirationDate);
      
      const response = await axios.post(
        `${API_URL}/api/purchase-orders/${selectedPO.id}/scan-receive?${params.toString()}`
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setScanHistory([{ ...response.data.item, timestamp: new Date(), success: true }, ...scanHistory]);
        
        // Refresh receiving status
        const statusRes = await axios.get(`${API_URL}/api/purchase-orders/${selectedPO.id}/receiving-status`);
        setReceivingStatus(statusRes.data);
        
        // Clear inputs for next scan
        setBarcodeInput('');
        setScanQuantity(1);
        setBatchNumber('');
        setExpirationDate('');
        
        // If PO is fully received, show success and refresh
        if (response.data.po_fully_received) {
          toast.success('Purchase Order fully received!');
          fetchData();
        }
      } else {
        toast.error(response.data.message);
        setScanHistory([{ barcode: barcodeInput, error: response.data.error, message: response.data.message, timestamp: new Date(), success: false }, ...scanHistory]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error processing scan');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: FileText },
      pending_approval: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      sent: { color: 'bg-purple-100 text-purple-800', icon: Send },
      partially_received: { color: 'bg-orange-100 text-orange-800', icon: Package },
      received: { color: 'bg-green-100 text-green-800', icon: Check },
      cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const cfg = config[status] || config.draft;
    const Icon = cfg.icon;
    return <Badge className={cfg.color}><Icon className="h-3 w-3 mr-1" />{t(`enterprise.purchaseOrders.statuses.${status}`)}</Badge>;
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('enterprise.purchaseOrders.title')}</h3>
          <p className="text-sm text-gray-500">{t('enterprise.purchaseOrders.subtitle')}</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-[#E63946]">
          <Plus className="h-4 w-4 mr-2" />{t('enterprise.purchaseOrders.newPO')}
        </Button>
      </div>

      {orders.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500"><ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />{t('enterprise.purchaseOrders.empty')}</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {orders.map(po => (
            <Card key={po.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <ShoppingCart className="h-6 w-6 text-indigo-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{po.po_number}</h4>
                        {getStatusBadge(po.status)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        <span>{po.supplier_name}</span>
                        <span> → {po.warehouse_name}</span>
                      </div>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span><strong>{po.items?.length || 0}</strong> {t('enterprise.purchaseOrders.items')}</span>
                        <span><strong>${(po.grand_total || 0).toLocaleString()}</strong></span>
                        {po.expected_date && <span><Calendar className="h-3 w-3 inline mr-1" />{po.expected_date}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {po.status === 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => handleAction(po.id, 'submit')}><Send className="h-4 w-4 mr-1" />{t('enterprise.purchaseOrders.actions.submit')}</Button>
                    )}
                    {po.status === 'pending_approval' && (
                      <Button size="sm" variant="outline" onClick={() => handleAction(po.id, 'approve')} className="text-green-600"><Check className="h-4 w-4 mr-1" />{t('enterprise.purchaseOrders.actions.approve')}</Button>
                    )}
                    {po.status === 'approved' && (
                      <Button size="sm" variant="outline" onClick={() => handleAction(po.id, 'send')} className="text-purple-600"><Send className="h-4 w-4 mr-1" />{t('enterprise.purchaseOrders.actions.send')}</Button>
                    )}
                    {['sent', 'partially_received'].includes(po.status) && (
                      <Button size="sm" variant="outline" onClick={() => openReceiveSheet(po)} className="text-green-600" data-testid={`receive-btn-${po.id}`}>
                        <Package className="h-4 w-4 mr-1" />{t('enterprise.purchaseOrders.actions.receive')}
                      </Button>
                    )}
                    {!['received', 'cancelled'].includes(po.status) && (
                      <Button size="sm" variant="ghost" onClick={() => handleAction(po.id, 'cancel')} className="text-red-600"><X className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('enterprise.purchaseOrders.newPO')}</DialogTitle>
            <DialogDescription>{t('enterprise.purchaseOrders.form.title')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.purchaseOrders.supplier')} *</Label>
                <Select value={formData.supplier_id} onValueChange={v => {
                  const sup = suppliers.find(s => s.id === v);
                  setFormData({...formData, supplier_id: v, supplier_name: sup?.name || ''});
                }}>
                  <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t('enterprise.purchaseOrders.warehouse')} *</Label>
                <Select value={formData.warehouse_id} onValueChange={v => {
                  const wh = warehouses.find(w => w.id === v);
                  setFormData({...formData, warehouse_id: v, warehouse_name: wh?.name || ''});
                }}>
                  <SelectTrigger><SelectValue placeholder="Select warehouse" /></SelectTrigger>
                  <SelectContent>
                    {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('enterprise.purchaseOrders.expectedDate')}</Label><Input type="date" value={formData.expected_date} onChange={e => setFormData({...formData, expected_date: e.target.value})} /></div>
            </div>

            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium">{t('enterprise.purchaseOrders.form.addItem')}</h4>
              <div className="flex gap-2 items-end">
                <div className="flex-1"><Label>{t('enterprise.purchaseOrders.form.medication')}</Label>
                  <Select value={newItem.medication_id} onValueChange={v => {
                    const med = medications.find(m => m.id === v);
                    setNewItem({...newItem, medication_id: v, medication_name: med?.generic_name || '', unit_price: med?.price || 0});
                  }}>
                    <SelectTrigger><SelectValue placeholder="Select medication" /></SelectTrigger>
                    <SelectContent>
                      {medications.map(m => <SelectItem key={m.id} value={m.id}>{m.generic_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-24"><Label>{t('enterprise.purchaseOrders.form.quantity')}</Label><Input type="number" min={1} value={newItem.quantity} onChange={e => setNewItem({...newItem, quantity: parseInt(e.target.value) || 1})} /></div>
                <div className="w-28"><Label>{t('enterprise.purchaseOrders.form.unitPrice')}</Label><Input type="number" step="0.01" value={newItem.unit_price} onChange={e => setNewItem({...newItem, unit_price: parseFloat(e.target.value) || 0})} /></div>
                <Button type="button" onClick={addItem} size="sm"><Plus className="h-4 w-4" /></Button>
              </div>

              {formData.items.length > 0 && (
                <div className="space-y-2 mt-4">
                  {formData.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span>{item.medication_name} × {item.quantity} @ ${item.unit_price}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <div className="text-right font-medium">
                    {t('enterprise.purchaseOrders.form.subtotal')}: ${formData.items.reduce((sum, i) => sum + i.quantity * i.unit_price, 0).toLocaleString()}
                  </div>
                </div>
              )}
            </div>

            <div><Label>{t('enterprise.purchaseOrders.form.notes')}</Label><Textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E63946]" disabled={!formData.supplier_id || !formData.warehouse_id}>Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Receiving Sheet with Barcode Scanner */}
      <Sheet open={showReceiveSheet} onOpenChange={setShowReceiveSheet}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              {t('enterprise.purchaseOrders.receive.title')} - {selectedPO?.po_number}
            </SheetTitle>
            <SheetDescription>
              {selectedPO?.supplier_name} → {selectedPO?.warehouse_name}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {/* Progress Overview */}
            {receivingStatus && (
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium">Overall Progress</span>
                  <span className="text-sm font-bold">{receivingStatus.overall_progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-green-500 h-2.5 rounded-full transition-all" 
                    style={{ width: `${receivingStatus.overall_progress}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>{receivingStatus.total_received} received</span>
                  <span>{receivingStatus.total_pending} pending</span>
                </div>
              </div>
            )}

            {/* Barcode Scanner Form */}
            <form onSubmit={handleBarcodeScan} className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-white">
                <div className="flex items-center gap-2 mb-3">
                  <Search className="h-5 w-5 text-gray-400" />
                  <span className="font-medium">Scan Barcode</span>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <Input
                      type="text"
                      placeholder="Scan or enter barcode..."
                      value={barcodeInput}
                      onChange={e => setBarcodeInput(e.target.value)}
                      className="text-lg font-mono"
                      autoFocus
                      data-testid="barcode-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">{t('enterprise.purchaseOrders.form.quantity')}</Label>
                      <Input 
                        type="number" 
                        min={1} 
                        value={scanQuantity} 
                        onChange={e => setScanQuantity(parseInt(e.target.value) || 1)}
                        data-testid="scan-quantity"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('enterprise.purchaseOrders.receive.batchNumber')}</Label>
                      <Input 
                        type="text" 
                        placeholder="Auto-generated"
                        value={batchNumber}
                        onChange={e => setBatchNumber(e.target.value)}
                        data-testid="batch-number"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">{t('enterprise.purchaseOrders.receive.expirationDate')}</Label>
                      <Input 
                        type="date"
                        value={expirationDate}
                        onChange={e => setExpirationDate(e.target.value)}
                        data-testid="expiration-date"
                      />
                    </div>
                  </div>
                  
                  <Button type="submit" className="w-full bg-[#E63946]" disabled={!barcodeInput.trim()}>
                    <Check className="h-4 w-4 mr-2" /> Receive Item
                  </Button>
                </div>
              </div>
            </form>

            {/* Scan History */}
            {scanHistory.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Recent Scans</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {scanHistory.map((scan, idx) => (
                    <div 
                      key={idx} 
                      className={`p-2 rounded text-sm flex items-center gap-2 ${
                        scan.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                      }`}
                    >
                      {scan.success ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <XCircle className="h-4 w-4" />
                      )}
                      <span className="flex-1">
                        {scan.success 
                          ? `${scan.quantity_received_now}× ${scan.medication_name} (Batch: ${scan.batch_number})`
                          : scan.message
                        }
                      </span>
                      <span className="text-xs opacity-60">
                        {new Date(scan.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Items Status Table */}
            {receivingStatus?.items && (
              <div>
                <h4 className="font-medium mb-2">Items to Receive</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Item</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Ordered</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Received</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Pending</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {receivingStatus.items.map((item, idx) => (
                        <tr key={idx} className={item.fully_received ? 'bg-green-50' : ''}>
                          <td className="px-3 py-2">
                            <div className="text-sm font-medium">{item.medication_name}</div>
                            {item.barcodes?.length > 0 && (
                              <div className="text-xs text-gray-400 font-mono">
                                {item.barcodes[0].barcode_value}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-center text-sm">{item.quantity_ordered}</td>
                          <td className="px-3 py-2 text-center text-sm font-medium text-green-600">{item.quantity_received}</td>
                          <td className="px-3 py-2 text-center text-sm font-medium text-orange-600">{item.quantity_pending}</td>
                          <td className="px-3 py-2 text-center">
                            {item.fully_received ? (
                              <Badge className="bg-green-100 text-green-800"><Check className="h-3 w-3 mr-1" />Done</Badge>
                            ) : (
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div 
                                  className="bg-blue-500 h-1.5 rounded-full" 
                                  style={{ width: `${item.progress_percent}%` }}
                                ></div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Close Button */}
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => { setShowReceiveSheet(false); fetchData(); }}
            >
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

// =============== TRANSFERS TAB ===============
const TransfersTab = ({ t }) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTransfers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stock-transfers`);
      setTransfers(response.data?.transfers || []);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchTransfers(); }, [fetchTransfers]);

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      in_transit: 'bg-purple-100 text-purple-800',
      received: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[status] || 'bg-gray-100'}>{t(`enterprise.transfers.statuses.${status}`)}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const colors = { low: 'bg-gray-100 text-gray-600', normal: 'bg-blue-100 text-blue-700', high: 'bg-orange-100 text-orange-700', urgent: 'bg-red-100 text-red-700' };
    return <Badge className={colors[priority] || 'bg-gray-100'}>{t(`enterprise.transfers.priorities.${priority}`)}</Badge>;
  };

  const handleAction = async (transferId, action) => {
    try {
      if (action === 'cancel') {
        const reason = prompt('Reason for cancellation:');
        if (!reason) return;
        await axios.put(`${API_URL}/api/stock-transfers/${transferId}/cancel?reason=${encodeURIComponent(reason)}`);
      } else {
        await axios.put(`${API_URL}/api/stock-transfers/${transferId}/${action}`);
      }
      toast.success('Action completed');
      fetchTransfers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('enterprise.transfers.title')}</h3>
          <p className="text-sm text-gray-500">{t('enterprise.transfers.subtitle')}</p>
        </div>
        <Button className="bg-[#E63946]" disabled>
          <Plus className="h-4 w-4 mr-2" />{t('enterprise.transfers.newTransfer')}
        </Button>
      </div>

      {transfers.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500"><ArrowLeftRight className="h-12 w-12 mx-auto mb-4 opacity-50" />{t('enterprise.transfers.empty')}</CardContent></Card>
      ) : (
        <div className="grid gap-4">
          {transfers.map(transfer => (
            <Card key={transfer.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center">
                      <ArrowLeftRight className="h-6 w-6 text-orange-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{transfer.transfer_number}</h4>
                        {getStatusBadge(transfer.status)}
                        {getPriorityBadge(transfer.priority)}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        <span>{transfer.from_warehouse_name}</span>
                        <span className="mx-2">→</span>
                        <span>{transfer.to_warehouse_name}</span>
                      </div>
                      <div className="flex gap-4 mt-2 text-sm">
                        <span><strong>{transfer.total_items}</strong> {t('enterprise.transfers.items')}</span>
                        <span><strong>{transfer.total_quantity}</strong> {t('enterprise.transfers.quantity')}</span>
                        <span className="text-gray-500">{transfer.reason}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {transfer.status === 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => handleAction(transfer.id, 'submit')}>{t('enterprise.transfers.actions.submit')}</Button>
                    )}
                    {transfer.status === 'pending_approval' && (
                      <Button size="sm" variant="outline" onClick={() => handleAction(transfer.id, 'approve')} className="text-green-600">{t('enterprise.transfers.actions.approve')}</Button>
                    )}
                    {transfer.status === 'approved' && (
                      <Button size="sm" variant="outline" onClick={() => handleAction(transfer.id, 'ship')} className="text-purple-600"><Truck className="h-4 w-4 mr-1" />{t('enterprise.transfers.actions.ship')}</Button>
                    )}
                    {transfer.status === 'in_transit' && (
                      <Button size="sm" variant="outline" onClick={() => handleAction(transfer.id, 'receive')} className="text-green-600"><Check className="h-4 w-4 mr-1" />{t('enterprise.transfers.actions.receive')}</Button>
                    )}
                    {!['received', 'cancelled'].includes(transfer.status) && (
                      <Button size="sm" variant="ghost" onClick={() => handleAction(transfer.id, 'cancel')} className="text-red-600"><X className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// =============== REORDER TAB ===============
const ReorderTab = ({ t }) => {
  const [rules, setRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, alertsRes] = await Promise.all([
        axios.get(`${API_URL}/api/reorder/rules`),
        axios.get(`${API_URL}/api/reorder/alerts?status_filter=pending`)
      ]);
      setRules(rulesRes.data?.rules || []);
      setAlerts(alertsRes.data?.alerts || []);
      setPendingCount(alertsRes.data?.pending_count || 0);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const checkNow = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/reorder/check`);
      toast.success(response.data?.message || 'Check completed');
      fetchData();
    } catch (error) {
      toast.error('Error checking reorder points');
    }
  };

  const dismissAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/reorder/alerts/${alertId}/dismiss`);
      toast.success('Alert dismissed');
      fetchData();
    } catch (error) {
      toast.error('Error dismissing alert');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('enterprise.reorder.title')}</h3>
          <p className="text-sm text-gray-500">{t('enterprise.reorder.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={checkNow}><RefreshCw className="h-4 w-4 mr-2" />{t('enterprise.reorder.checkNow')}</Button>
          <Button className="bg-[#E63946]" disabled><Plus className="h-4 w-4 mr-2" />{t('enterprise.reorder.newRule')}</Button>
        </div>
      </div>

      {pendingCount > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              {t('enterprise.reorder.alerts')} ({pendingCount})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.map(alert => (
                <div key={alert.id} className="flex items-center justify-between bg-white p-3 rounded border">
                  <div>
                    <div className="font-medium">{alert.medication_name}</div>
                    <div className="text-sm text-gray-500">
                      {alert.warehouse_name && <span>{alert.warehouse_name} • </span>}
                      {t('enterprise.reorder.currentStock')}: <span className="text-red-600 font-medium">{alert.current_stock}</span>
                      <span className="mx-2">•</span>
                      {t('enterprise.reorder.suggestedQty')}: {alert.suggested_quantity}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="text-green-600">{t('enterprise.reorder.actions.createPO')}</Button>
                    <Button size="sm" variant="ghost" onClick={() => dismissAlert(alert.id)}><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('enterprise.reorder.rules')}</CardTitle>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <div className="py-8 text-center text-gray-500">{t('enterprise.reorder.emptyRules')}</div>
          ) : (
            <div className="space-y-2">
              {rules.map(rule => (
                <div key={rule.id} className={`flex items-center justify-between p-3 rounded border ${rule.needs_reorder ? 'border-yellow-300 bg-yellow-50' : ''}`}>
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="font-medium">{rule.medication_name}</div>
                      <div className="text-sm text-gray-500">
                        {rule.warehouse_name && <span>{rule.warehouse_name} • </span>}
                        {t('enterprise.reorder.currentStock')}: <span className={rule.needs_reorder ? 'text-red-600 font-medium' : ''}>{rule.current_stock}</span>
                        <span className="mx-2">|</span>
                        {t('enterprise.reorder.reorderPoint')}: {rule.reorder_point}
                        <span className="mx-2">|</span>
                        {t('enterprise.reorder.reorderQty')}: {rule.reorder_quantity}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {rule.needs_reorder && <Badge className="bg-yellow-100 text-yellow-800"><AlertTriangle className="h-3 w-3 mr-1" />{t('enterprise.reorder.needsReorder')}</Badge>}
                    <Badge variant={rule.is_active ? 'default' : 'secondary'}>{rule.is_active ? 'Active' : 'Inactive'}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// =============== VALUATION TAB ===============
const ValuationTab = ({ t }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [abcData, setAbcData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, abcRes] = await Promise.all([
        axios.get(`${API_URL}/api/valuation/summary`),
        axios.get(`${API_URL}/api/valuation/abc-analysis`)
      ]);
      setSummary(summaryRes.data);
      setAbcData(abcRes.data);
    } catch (error) {
      toast.error(t('messages.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('enterprise.valuation.title')}</h3>
          <p className="text-sm text-gray-500">{t('enterprise.valuation.subtitle')}</p>
        </div>
        <Button className="bg-[#E63946]"><FileText className="h-4 w-4 mr-2" />{t('enterprise.valuation.generateReport')}</Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('enterprise.valuation.totalItems')}</p>
                <p className="text-2xl font-bold">{summary?.total_items || 0}</p>
              </div>
              <Package className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('enterprise.valuation.totalQuantity')}</p>
                <p className="text-2xl font-bold">{(summary?.total_quantity || 0).toLocaleString()}</p>
              </div>
              <BarChart3 className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('enterprise.valuation.totalValue')}</p>
                <p className="text-2xl font-bold">${(summary?.total_value || 0).toLocaleString()}</p>
              </div>
              <DollarSign className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('enterprise.valuation.avgItemValue')}</p>
                <p className="text-2xl font-bold">${(summary?.average_item_value || 0).toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ABC Analysis */}
      {abcData && (
        <Card>
          <CardHeader>
            <CardTitle>{t('enterprise.valuation.abcAnalysis')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-800">{t('enterprise.valuation.categoryA')}</h4>
                <div className="mt-2 space-y-1 text-sm">
                  <div><strong>{abcData.summary?.A?.items || 0}</strong> items</div>
                  <div><strong>${(abcData.summary?.A?.value || 0).toLocaleString()}</strong></div>
                  <div><strong>{abcData.summary?.A?.percentage || 0}%</strong> of total value</div>
                </div>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-semibold text-yellow-800">{t('enterprise.valuation.categoryB')}</h4>
                <div className="mt-2 space-y-1 text-sm">
                  <div><strong>{abcData.summary?.B?.items || 0}</strong> items</div>
                  <div><strong>${(abcData.summary?.B?.value || 0).toLocaleString()}</strong></div>
                  <div><strong>{abcData.summary?.B?.percentage || 0}%</strong> of total value</div>
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-800">{t('enterprise.valuation.categoryC')}</h4>
                <div className="mt-2 space-y-1 text-sm">
                  <div><strong>{abcData.summary?.C?.items || 0}</strong> items</div>
                  <div><strong>${(abcData.summary?.C?.value || 0).toLocaleString()}</strong></div>
                  <div><strong>{abcData.summary?.C?.percentage || 0}%</strong> of total value</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* By Warehouse */}
      {summary?.by_warehouse && summary.by_warehouse.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('enterprise.valuation.byWarehouse')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {summary.by_warehouse.map((wh, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex items-center gap-3">
                    <Warehouse className="h-5 w-5 text-gray-500" />
                    <span className="font-medium">{wh.name}</span>
                  </div>
                  <div className="flex gap-6 text-sm">
                    <span><strong>{wh.items}</strong> items</span>
                    <span><strong>{wh.quantity}</strong> units</span>
                    <span><strong>${wh.value.toLocaleString()}</strong></span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// =============== BARCODE PRINTING TAB ===============
const BarcodePrintingTab = ({ t }) => {
  const [medications, setMedications] = useState([]);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [barcodes, setBarcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatedLabels, setGeneratedLabels] = useState([]);
  const [printOptions, setPrintOptions] = useState({
    quantity: 1,
    batchNumber: '',
    expirationDate: '',
    includeQr: false,
    labelWidth: 400,
    labelHeight: 200
  });
  const [newBarcodeValue, setNewBarcodeValue] = useState('');
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);

  const fetchMedications = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/medications/catalog`);
      setMedications(response.data || []);
    } catch (error) {
      toast.error('Error loading medications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMedications(); }, [fetchMedications]);

  const fetchBarcodes = async (medicationId) => {
    try {
      const response = await axios.get(`${API_URL}/api/barcodes/medications/${medicationId}`);
      setBarcodes(response.data || []);
    } catch (error) {
      setBarcodes([]);
    }
  };

  const handleSelectMedication = async (med) => {
    setSelectedMedication(med);
    setGeneratedLabels([]);
    await fetchBarcodes(med.id);
  };

  const registerBarcode = async () => {
    if (!selectedMedication) return;
    
    try {
      const params = new URLSearchParams();
      if (newBarcodeValue) params.append('barcode_value', newBarcodeValue);
      params.append('barcode_type', 'code128');
      params.append('is_primary', 'true');
      
      const response = await axios.post(
        `${API_URL}/api/barcode-print/medication/${selectedMedication.id}/register-barcode?${params.toString()}`
      );
      
      toast.success(`Barcode registered: ${response.data.barcode_value}`);
      setShowRegisterDialog(false);
      setNewBarcodeValue('');
      await fetchBarcodes(selectedMedication.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error registering barcode');
    }
  };

  const generateLabels = async () => {
    if (!selectedMedication) return;
    
    try {
      const params = new URLSearchParams({
        quantity: printOptions.quantity,
        label_width: printOptions.labelWidth,
        label_height: printOptions.labelHeight,
        include_qr: printOptions.includeQr
      });
      if (printOptions.batchNumber) params.append('batch_number', printOptions.batchNumber);
      if (printOptions.expirationDate) params.append('expiration_date', printOptions.expirationDate);
      
      const response = await axios.get(
        `${API_URL}/api/barcode-print/medication/${selectedMedication.id}/labels?${params.toString()}`
      );
      
      setGeneratedLabels(response.data.labels || []);
      toast.success(`Generated ${response.data.total_labels} labels`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error generating labels');
    }
  };

  const printLabels = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print labels');
      return;
    }
    
    const labelsHtml = generatedLabels.map(label => 
      `<div style="display: inline-block; margin: 5px; page-break-inside: avoid;">
        <img src="${label.image_base64}" alt="Label ${label.index}" style="border: 1px solid #ccc;" />
      </div>`
    ).join('');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print Labels - ${selectedMedication?.generic_name}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            @media print {
              body { margin: 0; }
              div { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${labelsHtml}
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t('enterprise.barcodes.title')}</h3>
          <p className="text-sm text-gray-500">{t('enterprise.barcodes.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Medication Selection */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Select Medication</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {medications.map(med => (
                <button
                  key={med.id}
                  onClick={() => handleSelectMedication(med)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedMedication?.id === med.id 
                      ? 'border-[#E63946] bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium text-sm">{med.generic_name}</div>
                  {med.brand_name && <div className="text-xs text-gray-500">{med.brand_name}</div>}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Barcode Configuration */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Label Configuration</span>
              {selectedMedication && (
                <Button size="sm" variant="outline" onClick={() => setShowRegisterDialog(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Register Barcode
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedMedication ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Select a medication to generate labels</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Selected Medication Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium">{selectedMedication.generic_name}</h4>
                  {selectedMedication.brand_name && (
                    <p className="text-sm text-gray-500">{selectedMedication.brand_name}</p>
                  )}
                  
                  {/* Registered Barcodes */}
                  <div className="mt-3">
                    <span className="text-xs font-medium text-gray-500 uppercase">Registered Barcodes:</span>
                    {barcodes.length === 0 ? (
                      <p className="text-sm text-orange-600 mt-1">No barcodes registered. Click "Register Barcode" to add one.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {barcodes.map((bc, idx) => (
                          <Badge key={idx} variant={bc.is_primary ? 'default' : 'outline'} className="font-mono text-xs">
                            {bc.barcode_value}
                            {bc.is_primary && <span className="ml-1 text-[10px]">(primary)</span>}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Print Options */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Quantity</Label>
                    <Input 
                      type="number" 
                      min={1} 
                      max={100}
                      value={printOptions.quantity}
                      onChange={e => setPrintOptions({...printOptions, quantity: parseInt(e.target.value) || 1})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Batch Number</Label>
                    <Input 
                      placeholder="Optional"
                      value={printOptions.batchNumber}
                      onChange={e => setPrintOptions({...printOptions, batchNumber: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Expiration Date</Label>
                    <Input 
                      type="date"
                      value={printOptions.expirationDate}
                      onChange={e => setPrintOptions({...printOptions, expirationDate: e.target.value})}
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2">
                      <Checkbox 
                        checked={printOptions.includeQr}
                        onCheckedChange={c => setPrintOptions({...printOptions, includeQr: c})}
                      />
                      <span className="text-sm">Include QR Code</span>
                    </label>
                  </div>
                </div>

                <Button onClick={generateLabels} className="w-full bg-[#E63946]">
                  <FileText className="h-4 w-4 mr-2" /> Generate Labels
                </Button>

                {/* Generated Labels Preview */}
                {generatedLabels.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Preview ({generatedLabels.length} labels)</h4>
                      <Button onClick={printLabels} variant="outline">
                        <Eye className="h-4 w-4 mr-2" /> Print Labels
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3 max-h-[300px] overflow-y-auto p-2 bg-gray-50 rounded-lg">
                      {generatedLabels.map(label => (
                        <div key={label.index} className="bg-white rounded shadow-sm overflow-hidden">
                          <img 
                            src={label.image_base64} 
                            alt={`Label ${label.index}`}
                            className="w-full h-auto"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Register Barcode Dialog */}
      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Register Barcode</DialogTitle>
            <DialogDescription>
              Register a barcode for {selectedMedication?.generic_name}. Leave empty to auto-generate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Barcode Value</Label>
              <Input 
                placeholder="Auto-generate if empty"
                value={newBarcodeValue}
                onChange={e => setNewBarcodeValue(e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter existing barcode or leave empty to generate a new one
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegisterDialog(false)}>Cancel</Button>
            <Button onClick={registerBarcode} className="bg-[#E63946]">Register</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =============== MAIN COMPONENT ===============
const EnterpriseInventoryPage = () => {
  const { t } = useTranslation(['inventory', 'common']);
  const [activeTab, setActiveTab] = useState('warehouses');

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('title')} - Enterprise</h1>
        <p className="text-gray-500">{t('subtitle')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="warehouses">
            <Warehouse className="h-4 w-4" />{t('tabs.warehouses')}
          </TabsTrigger>
          <TabsTrigger value="suppliers">
            <Building2 className="h-4 w-4" />{t('tabs.suppliers')}
          </TabsTrigger>
          <TabsTrigger value="purchaseOrders">
            <ShoppingCart className="h-4 w-4" />{t('tabs.purchaseOrders')}
          </TabsTrigger>
          <TabsTrigger value="transfers">
            <ArrowLeftRight className="h-4 w-4" />{t('tabs.transfers')}
          </TabsTrigger>
          <TabsTrigger value="reorder">
            <AlertTriangle className="h-4 w-4" />{t('tabs.reorder')}
          </TabsTrigger>
          <TabsTrigger value="valuation">
            <BarChart3 className="h-4 w-4" />{t('tabs.valuation')}
          </TabsTrigger>
          <TabsTrigger value="barcodes">
            <FileText className="h-4 w-4" />Barcodes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="warehouses" className="mt-6">
          <WarehousesTab t={t} />
        </TabsContent>
        <TabsContent value="suppliers" className="mt-6">
          <SuppliersTab t={t} />
        </TabsContent>
        <TabsContent value="purchaseOrders" className="mt-6">
          <PurchaseOrdersTab t={t} />
        </TabsContent>
        <TabsContent value="transfers" className="mt-6">
          <TransfersTab t={t} />
        </TabsContent>
        <TabsContent value="reorder" className="mt-6">
          <ReorderTab t={t} />
        </TabsContent>
        <TabsContent value="valuation" className="mt-6">
          <ValuationTab t={t} />
        </TabsContent>
        <TabsContent value="barcodes" className="mt-6">
          <BarcodePrintingTab t={t} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnterpriseInventoryPage;
